// Google Sheet connector — setup card, inside Practice Details ("Integrations & features").
//
// Props: { practice, source, onChanged }. `source` is the resolved source object
// ({key, label, reason, capabilities}); `onChanged()` asks Practice Details to re-read
// the gate, because a successful layout check changes what this practice supplies.
//
// WHEN IT RENDERS. Not only for a practice whose source is already `sheet`: that is a
// chicken-and-egg, since a practice being SET UP is not a sheet practice yet and the
// card is how it becomes one. So it also renders for a practice with no resolved source
// at all — precisely the practice you would connect a sheet to — and stays hidden for a
// practice that already has a real feed, where offering a spreadsheet would be noise.
//
// THERE IS NO "SYNC NOW" BUTTON, deliberately. The sync is in-VPC only, via the
// scheduled job or prod-run.sh: a browser-triggered sync would run outside the VPC and,
// on a practice's whole book, inside a request timeout. The backend has no such route
// either, and a test asserts it.

import React, { useCallback, useEffect, useState } from "react";
import "./SheetConnectorCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const LAYOUT_LABELS = {
  canonical: "Standard tracker",
  olympus: "Olympus workbook",
};

const LAYOUT_HINTS = {
  canonical:
    "One tab per record type: MB_Pts, Claims, and optionally Avon, Auths, Copays.",
  olympus: "One tab per biller per month, plus a Patients roster.",
};

// Mirrors the backend's capability names so the card can say what the workbook
// actually feeds, rather than listing tabs and leaving the reader to work it out.
const CAPABILITY_LABELS = {
  schedule: "Schedule",
  coverages: "Insurance",
  auths: "Authorizations",
  charges: "Charges",
  statuses: "Statuses",
  remits: "Remittances",
};

function formatWhen(value) {
  if (!value) return null;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleString();
}

export default function SheetConnectorCard({ practice, source, onChanged }) {
  const practiceId = practice?.id;
  const isSheet = source?.key === "sheet" || practice?.pm_system === "sheet";
  // A practice with no feed at all is a candidate; one with a real feed is not.
  const couldBeSheet = !source?.key;
  const visible = Boolean(practiceId) && (isSheet || couldBeSheet);

  const [config, setConfig] = useState(null);
  const [sheetId, setSheetId] = useState("");
  const [layout, setLayout] = useState("canonical");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'save' | 'check'
  const [message, setMessage] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  const load = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/sheet`,
        {
          credentials: "include",
        },
      );
      if (!response.ok) {
        // 403 is the ordinary case for a non-admin, not a failure worth shouting
        // about: the card simply has nothing to offer them.
        if (response.status !== 403) {
          setMessage({
            type: "error",
            text: "Could not read the sheet configuration.",
          });
        }
        setConfig(null);
        return;
      }
      const body = await response.json();
      setConfig(body);
      setSheetId(body.sheet_id || "");
      setLayout(body.layout || "canonical");
    } catch (err) {
      setMessage({
        type: "error",
        text: "Could not read the sheet configuration.",
      });
    } finally {
      setLoading(false);
    }
  }, [practiceId, visible]);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    setBusy("save");
    setMessage(null);
    // The tab set the gate reads describes the workbook that was checked, so a
    // change invalidates it server-side; clear the stale result here too rather
    // than leaving a green tick above a sheet nobody has read.
    setCheckResult(null);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/sheet`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sheet_id: sheetId, layout }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage({
          type: "error",
          text: body.error || "Could not save that.",
        });
        return;
      }
      setMessage({ type: "success", text: "Saved. Check the layout next." });
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setMessage({ type: "error", text: "Could not save that." });
    } finally {
      setBusy(null);
    }
  }

  async function check() {
    setBusy("check");
    setMessage(null);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/sheet/check`,
        { method: "POST", credentials: "include" },
      );
      if (!response.ok) {
        // Clear the previous result too: an error sitting under a stale
        // "Layout looks right — safe to sync" is worse than no answer.
        setCheckResult(null);
        setMessage({ type: "error", text: "Could not run the layout check." });
        return;
      }
      // Always a body with ok/problems: "not configured" and "cannot open it" are
      // findings this card should show, not errors it should swallow.
      setCheckResult(await response.json());
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setCheckResult(null);
      setMessage({ type: "error", text: "Could not run the layout check." });
    } finally {
      setBusy(null);
    }
  }

  if (!visible) return null;

  const serviceAccount = config?.service_account;
  const lastSync = formatWhen(config?.last_sync_at);
  const lastCheck = formatWhen(config?.checked_at);
  const capabilities = checkResult?.capabilities || config?.capabilities || [];
  const dirty = config
    ? sheetId !== (config.sheet_id || "") ||
      layout !== (config.layout || "canonical")
    : Boolean(sheetId);

  return (
    <div className="gate-sheet-card sheet-card">
      <h4>Google Sheet</h4>
      <p>
        Atlas reads this workbook and never writes to it. Share it as{" "}
        <strong>Viewer</strong> with the Atlas service account, then paste the
        workbook&rsquo;s id below.
      </p>
      {serviceAccount && (
        <div className="sheet-sa-block">
          <span className="sheet-label">Share with</span>
          <code className="sheet-sa">{serviceAccount}</code>
        </div>
      )}
      {!serviceAccount && !loading && (
        <p className="sheet-warn">
          No service-account key is configured on this server, so the sync
          cannot read any workbook yet.
        </p>
      )}

      {message && (
        <div className={`sheet-message sheet-message-${message.type}`}>
          <span>{message.text}</span>
          <button
            type="button"
            className="sheet-dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      <label className="sheet-label" htmlFor={`sheet-id-${practiceId}`}>
        Sheet id
      </label>
      <input
        id={`sheet-id-${practiceId}`}
        className="sheet-input"
        type="text"
        value={sheetId}
        placeholder="the long token in the workbook's URL, between /d/ and /edit"
        onChange={(e) => setSheetId(e.target.value.trim())}
        disabled={loading || busy !== null}
      />

      <fieldset className="sheet-layouts">
        <legend className="sheet-label">Layout</legend>
        {(config?.layouts || ["canonical", "olympus"]).map((key) => (
          <label key={key} className="sheet-radio">
            <input
              type="radio"
              name={`sheet-layout-${practiceId}`}
              value={key}
              checked={layout === key}
              onChange={() => setLayout(key)}
              disabled={loading || busy !== null}
            />
            <span>{LAYOUT_LABELS[key] || key}</span>
            {LAYOUT_HINTS[key] && <em>{LAYOUT_HINTS[key]}</em>}
          </label>
        ))}
      </fieldset>

      <div className="sheet-actions">
        <button
          type="button"
          className="sheet-btn sheet-btn-primary"
          onClick={save}
          disabled={busy !== null || loading || !sheetId || !dirty}
        >
          {busy === "save" ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="sheet-btn"
          onClick={check}
          disabled={busy !== null || loading || !config?.configured || dirty}
          title={dirty ? "Save the change first" : "Reads the header rows only"}
        >
          {busy === "check" ? "Checking…" : "Check layout"}
        </button>
      </div>

      {checkResult && (
        <div
          className={`sheet-result ${checkResult.ok ? "sheet-result-ok" : "sheet-result-bad"}`}
        >
          <div className="sheet-result-head">
            {checkResult.ok
              ? "Layout looks right — safe to sync."
              : "Not safe to sync yet."}
          </div>
          {(checkResult.problems || []).length > 0 && (
            <ul className="sheet-problems">
              {checkResult.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
          {(checkResult.tabs_found || []).length > 0 && (
            <div className="sheet-tabs">
              Tabs found: {checkResult.tabs_found.join(", ")}
            </div>
          )}
        </div>
      )}

      {capabilities.length > 0 && (
        <div className="gate-chips sheet-chips">
          {capabilities.map((cap) => (
            <span key={cap} className="gate-chip on">
              {CAPABILITY_LABELS[cap] || cap}
            </span>
          ))}
        </div>
      )}

      <div className="sheet-status">
        {lastCheck
          ? `Layout last checked ${lastCheck}.`
          : "Layout has not been checked yet."}{" "}
        {lastSync ? `Last synced ${lastSync}.` : "Never synced."}
      </div>
      <p className="sheet-footnote">
        Syncing runs inside the VPC on a schedule. There is no sync button here.
      </p>
    </div>
  );
}
